import React, { useCallback, useEffect, useState } from 'react';
import EmailLinks from '../components/EmailLinks';
import EmailReader from '../components/EmailReader';
import { emailApi, customerApi, formatApiError } from '../services/api';
import { toast } from 'sonner';
import {
  Envelope,
  ArrowClockwise,
  EnvelopeOpen,
  PencilSimple,
  X,
  Paperclip,
  PaperPlaneTilt
} from '@phosphor-icons/react';

const formatDate = (iso) => {
  if (!iso) return '';

  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';

  const today = new Date();

  if (d.toDateString() === today.toDateString()) {
    return d.toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  return d.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit'
  });
};

const Emails = () => {
  const [box, setBox] = useState('inbox');
  const [search, setSearch] = useState('');
  const [draftId, setDraftId] = useState(null);
  const [retained, setRetained] = useState([]);
  const [links, setLinks] = useState({customer_id:null,booking_id:null});
  const [requestId, setRequestId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [account, setAccount] = useState('');
  const [selectedMail, setSelectedMail] = useState(null);
  const unread = messages.filter(mail => !mail.seen).length;
  const markRead = useCallback((uid) => {
    setMessages(current => current.map(mail => String(mail.uid) === String(uid) ? { ...mail, seen: true } : mail));
  }, []);

  const [composeOpen, setComposeOpen] = useState(false);
  const [replyToUid, setReplyToUid] = useState(null);
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);

  // Kundenauswahl für neue E-Mails
  const [recipientMode, setRecipientMode] = useState('customer');
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [customerSearching, setCustomerSearching] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const searchCustomers = async (value) => {
    setCustomerSearch(value);
    setSelectedCustomer(null);

    if (value.trim().length < 2) {
      setCustomerResults([]);
      return;
    }

    setCustomerSearching(true);

    try {
      const res = await customerApi.getAll({
        search: value.trim()
      });

      setCustomerResults((res.data || []).slice(0, 10));
    } catch (e) {
      toast.error(formatApiError(e));
      setCustomerResults([]);
    } finally {
      setCustomerSearching(false);
    }
  };

  const selectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setLinks({customer_id:customer.id,booking_id:null});
    setTo(customer.email || '');
    setCustomerSearch(
      [customer.first_name, customer.last_name]
        .filter(Boolean)
        .join(' ') ||
      customer.company_name ||
      customer.email ||
      ''
    );
    setCustomerResults([]);
  };

  const loadEmails = async (refresh = false) => {
    if (refresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const res = await emailApi.getMessages(100, { box, search });
      setMessages(res.data?.messages || []);
      setAccount(res.data?.email || '');
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadEmails();
  }, [box]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetCompose = () => {
    setDraftId(null); setRetained([]); setLinks({customer_id:null,booking_id:null}); setRequestId(null);
    setTo('');
    setSubject('');
    setBody('');
    setFiles([]);
    setReplyToUid(null);
    setSelectedCustomer(null);
    setCustomerSearch('');
    setCustomerResults([]);
  };

  const startReply = (message) => {
    resetCompose();
    setTo(message.reply_to_email || message.from_email || '');
    setSubject(/^(re|aw|antw):\s*/i.test(message.subject || '') ? message.subject : `Re: ${message.subject || '(Kein Betreff)'}`);
    setRecipientMode('manual');
    setReplyToUid(message.uid);
    setLinks({customer_id:message.customer_id||null,booking_id:message.booking_id||null});
    setSelectedMail(null);
    setComposeOpen(true);
  };

  const closeCompose = () => {
    if (sending) return;
    if ((to || body || subject || files.length) && !window.confirm('E-Mail schließen? Ungespeicherte Änderungen werden verworfen. Zum Behalten bitte als Entwurf speichern.')) return;
    setComposeOpen(false);
    resetCompose();
  };

  const handleSend = async (e) => {
    e.preventDefault();

    if (!to.trim()) {
      toast.error('Bitte einen Empfänger eingeben.');
      return;
    }

    if (!body.trim()) {
      toast.error('Bitte eine Nachricht eingeben.');
      return;
    }

    const formData = new FormData();
    formData.append('to_email', to.trim());
    formData.append('subject', subject);
    formData.append('body', body);
    if (replyToUid) formData.append('reply_to_uid', replyToUid);
    if (draftId) formData.append('draft_id', draftId);
    if (links.customer_id) formData.append('customer_id', links.customer_id);
    if (links.booking_id) formData.append('booking_id', links.booking_id);
    formData.append('retained_files', JSON.stringify(retained.map(f=>f.id)));
    const sendId = requestId || (globalThis.crypto?.randomUUID?.() || `mail-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    setRequestId(sendId); formData.append('request_id', sendId);

    files.forEach((file) => {
      formData.append('files', file);
    });

    setSending(true);

    try {
      await emailApi.send(formData);
      toast.success('E-Mail wurde erfolgreich versendet.');
      setComposeOpen(false);
      resetCompose(); loadEmails();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const openDraft = async mail => {try {const r=await emailApi.getMessage(mail.uid);resetCompose();setDraftId(r.data.uid);setTo(r.data.to||'');setSubject(r.data.subject||'');setBody(r.data.body_plain||'');setReplyToUid(r.data.reply_to_uid||null);setRetained(r.data.attachments||[]);setLinks({customer_id:r.data.customer_id||null,booking_id:r.data.booking_id||null});setRecipientMode('manual');setComposeOpen(true);}catch(e){toast.error(formatApiError(e));}};
  const saveDraft = async () => {setSending(true);try{const data=new FormData();data.append('to_email',to);data.append('subject',subject);data.append('body',body);if(draftId)data.append('draft_id',draftId);if(replyToUid)data.append('reply_to_uid',replyToUid);if(links.customer_id)data.append('customer_id',links.customer_id);if(links.booking_id)data.append('booking_id',links.booking_id);data.append('retained_files',JSON.stringify(retained.map(f=>f.id)));files.forEach(file=>data.append('files',file));await emailApi.saveDraft(data);toast.success('Entwurf gespeichert');setComposeOpen(false);resetCompose();loadEmails();}catch(e){toast.error(formatApiError(e));}finally{setSending(false);}};
  return (
    <div className="p-6 space-y-6" data-testid="emails-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-[#111418] flex items-center gap-2">
            <Envelope weight="bold" size={30} />
            E-Mails
          </h1>

          <p className="text-[#525866] mt-1">
            {account
              ? `${account} · ${unread} ungelesen`
              : 'T-Rent E-Mail-Postfach'}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => loadEmails(true)}
            disabled={refreshing}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[#D8DCE3] bg-white hover:bg-[#F8F9FB] transition disabled:opacity-50"
          >
            <ArrowClockwise
              size={18}
              className={refreshing ? 'animate-spin' : ''}
            />
            Aktualisieren
          </button>

          <button
            type="button"
            onClick={() => { resetCompose(); setRecipientMode('customer'); setComposeOpen(true); }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#111418] text-white hover:bg-[#2A2F36] transition"
          >
            <PencilSimple size={18} />
            Neue E-Mail
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">{[['inbox','Posteingang'],['sent','Gesendet'],['drafts','Entwürfe']].map(([key,label])=><button key={key} type="button" aria-pressed={box===key} className={box===key?'px-4 py-2 bg-[#111418] text-white rounded':'px-4 py-2 border rounded'} onClick={()=>{setBox(key);setSelectedMail(null);}}>{label}</button>)}</div>
      <form className="flex gap-2" onSubmit={e=>{e.preventDefault();loadEmails();}}><input aria-label="E-Mails durchsuchen" className="border p-2 rounded flex-1" placeholder="E-Mails durchsuchen …" value={search} onChange={e=>setSearch(e.target.value)}/><button type="submit" className="border px-4 rounded">Suchen</button></form>
      {box==='sent'&&<p className="text-xs text-[#525866]">Hier erscheinen ab jetzt über diese Anwendung versendete E-Mails. Bei unbestätigtem Versand bitte vor erneutem Senden das Postfach prüfen.</p>}
      {loading ? (
        <div className="card-swiss p-12 text-center text-[#525866]">
          E-Mails werden geladen …
        </div>
      ) : messages.length === 0 ? (
        <div className="card-swiss p-12 text-center text-[#525866]">
          <EnvelopeOpen
            size={42}
            className="mx-auto mb-3 text-[#8A94A6]"
          />
          <p className="font-semibold">Keine E-Mails vorhanden</p>
        </div>
      ) : (
        <div className="card-swiss overflow-hidden">
          <div className="px-4 py-3 border-b border-[#E2E4E9] flex items-center justify-between">
            <div className="font-semibold text-[#111418]">
              Posteingang
            </div>

            <div className="text-sm text-[#8A94A6]">
              {messages.length} E-Mails
            </div>
          </div>

          <ul className="divide-y divide-[#E2E4E9]">
            {messages.map((mail) => (
              <li key={mail.uid}>
                <button
                  type="button"
                  onClick={() => box==='drafts' ? openDraft(mail) : setSelectedMail(mail)}
                  aria-label={`E-Mail öffnen: ${mail.subject || '(Kein Betreff)'}`}
                  className={`w-full text-left px-4 py-4 hover:bg-[#F8F9FB] transition ${
                    !mail.seen ? 'bg-blue-50/50' : 'bg-white'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="pt-1">
                      {mail.seen ? (
                        <EnvelopeOpen
                          size={20}
                          className="text-[#8A94A6]"
                        />
                      ) : (
                        <Envelope
                          size={20}
                          weight="fill"
                          className="text-[#111418]"
                        />
                      )}
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-4">
                        <div
                          className={`truncate ${
                            !mail.seen
                              ? 'font-bold text-[#111418]'
                              : 'font-medium text-[#343A40]'
                          }`}
                        >
                          {mail.from_name || mail.from_email || 'Unbekannt'}
                        </div>

                        <div className="text-xs text-[#8A94A6] whitespace-nowrap">
                          {formatDate(mail.date_iso)}
                        </div>
                      </div>

                      <div
                        className={`truncate mt-1 ${
                          !mail.seen
                            ? 'font-semibold text-[#111418]'
                            : 'text-[#525866]'
                        }`}
                      >
                        {mail.subject || '(Kein Betreff)'}
                      </div>

                      {mail.from_name && mail.from_email && (
                        <div className="text-xs text-[#8A94A6] truncate mt-1">
                          {box==='inbox'?mail.from_email:mail.to}{mail.status==='uncertain'?' · Versand unbestätigt':mail.status==='sending'?' · Versand läuft':''}
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <EmailReader mail={selectedMail} onClose={() => setSelectedMail(null)} onRead={markRead} onReply={startReply} />

      {composeOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E2E4E9]">
              <div>
                <h2 className="text-xl font-semibold text-[#111418]">
                  {replyToUid ? 'E-Mail beantworten' : 'Neue E-Mail'}
                </h2>
                <p className="text-sm text-[#8A94A6] mt-1">
                  Von: {account || 'T-Rent'}
                </p>
              </div>

              <button
                type="button"
                onClick={closeCompose}
                className="p-2 rounded-lg hover:bg-[#F3F4F6]"
              >
                <X size={22} />
              </button>
            </div>

            <form onSubmit={handleSend} className="flex flex-col min-h-0 flex-1">
              <div className="p-6 space-y-4 overflow-y-auto flex-1">
                <div>
                  <label className="block text-sm font-medium text-[#343A40] mb-2">
                    Empfänger
                  </label>

                  <div className="flex gap-2 mb-3">
                    <button
                      type="button"
                      onClick={() => {
                        setRecipientMode('customer');
                        setTo('');
                        setCustomerSearch('');
                        setCustomerResults([]);
                        setSelectedCustomer(null);
                      }}
                      className={`px-3 py-2 rounded-lg border text-sm ${
                        recipientMode === 'customer'
                          ? 'bg-[#111418] text-white border-[#111418]'
                          : 'bg-white text-[#343A40] border-[#D8DCE3]'
                      }`}
                    >
                      Kunde auswählen
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setRecipientMode('manual');
                        setTo('');
                        setCustomerSearch('');
                        setCustomerResults([]);
                        setSelectedCustomer(null);
                      }}
                      className={`px-3 py-2 rounded-lg border text-sm ${
                        recipientMode === 'manual'
                          ? 'bg-[#111418] text-white border-[#111418]'
                          : 'bg-white text-[#343A40] border-[#D8DCE3]'
                      }`}
                    >
                      E-Mail manuell
                    </button>
                  </div>

                  {recipientMode === 'customer' ? (
                    <div className="relative">
                      <input
                        type="text"
                        value={customerSearch}
                        onChange={(e) => searchCustomers(e.target.value)}
                        placeholder="Name, Firma, Kundennummer oder E-Mail suchen …"
                        className="w-full px-3 py-2.5 border border-[#D8DCE3] rounded-lg outline-none focus:ring-2 focus:ring-black/10"
                        autoFocus
                      />

                      {customerSearching && (
                        <div className="text-sm text-[#8A94A6] mt-2">
                          Kunden werden gesucht …
                        </div>
                      )}

                      {customerResults.length > 0 && (
                        <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-[#D8DCE3] rounded-lg shadow-lg max-h-64 overflow-y-auto">
                          {customerResults.map((customer) => {
                            const name =
                              [customer.first_name, customer.last_name]
                                .filter(Boolean)
                                .join(' ') ||
                              customer.company_name ||
                              'Unbekannter Kunde';

                            return (
                              <button
                                key={customer.id}
                                type="button"
                                onClick={() => selectCustomer(customer)}
                                className="w-full text-left px-4 py-3 hover:bg-[#F8F9FB] border-b border-[#F0F1F3] last:border-0"
                              >
                                <div className="font-medium text-[#111418]">
                                  {name}
                                </div>

                                <div className="text-sm text-[#525866] mt-0.5">
                                  {customer.email || 'Keine E-Mail hinterlegt'}
                                </div>

                                {customer.customer_number && (
                                  <div className="text-xs text-[#8A94A6] mt-0.5">
                                    Kundennr. {customer.customer_number}
                                  </div>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {selectedCustomer && (
                        <div className="mt-2 px-3 py-2 rounded-lg bg-[#F3F4F6] text-sm">
                          <span className="font-medium">
                            Ausgewählter Kunde:
                          </span>{' '}
                          {customerSearch}
                          <span className="text-[#525866]">
                            {' '}· {selectedCustomer.email || 'Keine E-Mail'}
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <input
                      type="email"
                      value={to}
                      onChange={(e) => setTo(e.target.value)}
                      placeholder="kunde@example.de"
                      className="w-full px-3 py-2.5 border border-[#D8DCE3] rounded-lg outline-none focus:ring-2 focus:ring-black/10"
                      autoFocus
                      required
                    />
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#343A40] mb-1">
                    Betreff
                  </label>
                  <input
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Betreff"
                    className="w-full px-3 py-2.5 border border-[#D8DCE3] rounded-lg outline-none focus:ring-2 focus:ring-black/10"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#343A40] mb-1">
                    Nachricht
                  </label>
                  <textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder="Nachricht schreiben …"
                    rows={12}
                    className="w-full px-3 py-3 border border-[#D8DCE3] rounded-lg outline-none focus:ring-2 focus:ring-black/10 resize-y"
                    required
                  />
                </div>

                <div>
                  <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[#D8DCE3] cursor-pointer hover:bg-[#F8F9FB]">
                    <Paperclip size={18} />
                    Datei anhängen
                    <input
                      type="file"
                      multiple
                      className="hidden"
                      onChange={(e) => setFiles(Array.from(e.target.files || []))}
                    />
                  </label>

                  {files.length > 0 && (
                    <div className="mt-3 space-y-1">
                      {files.map((file, index) => (
                        <div
                          key={`${file.name}-${index}`}
                          className="text-sm text-[#525866]"
                        >
                          {file.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {retained.length>0&&<div className="px-6 text-sm">{retained.map(f=><label key={f.id} className="block"><input type="checkbox" checked onChange={()=>setRetained(rows=>rows.filter(x=>x.id!==f.id))}/> {f.filename} (beibehalten)</label>)}</div>}
              <div className="px-6 pb-3"><EmailLinks key={links.customer_id||'none'} localOnly message={links} onChange={setLinks}/></div>
              <div className="px-6 py-4 border-t border-[#E2E4E9] flex justify-end gap-3 bg-white shrink-0">
                <button
                  type="button"
                  onClick={closeCompose}
                  disabled={sending}
                  className="px-4 py-2 rounded-lg border border-[#D8DCE3] bg-white hover:bg-[#F8F9FB] disabled:opacity-50"
                >
                  Abbrechen
                </button>

                <button type="button" disabled={sending} className="px-4 py-2 border rounded-lg" onClick={saveDraft}>Als Entwurf speichern</button>
                {draftId&&<button type="button" disabled={sending} className="px-4 py-2 border rounded-lg" onClick={async()=>{if(!window.confirm('Diesen Entwurf löschen?'))return;setSending(true);try{await emailApi.deleteDraft(draftId);setComposeOpen(false);resetCompose();loadEmails();}catch(e){toast.error(formatApiError(e));}finally{setSending(false);}}}>Entwurf löschen</button>}
                <button
                  type="submit"
                  disabled={sending}
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-[#111418] text-white hover:bg-[#2A2F36] disabled:opacity-50"
                >
                  <PaperPlaneTilt size={18} weight="fill" />
                  {sending ? 'Wird gesendet …' : 'Senden'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Emails;
